/**
 * Inkoop-betaallijst (batch-14 FO-017).
 *
 * Een betaallijst is een opgeslagen, auditbaar object: een momentopname van de
 * te betalen inkoopfacturen (status OPEN/GEBOEKT), gemaakt vanuit een handmatige
 * selectie OF een filter. Daaruit volgt een Excel-overzicht + een SEPA-bestand,
 * waarna de facturen als betaald gemarkeerd kunnen worden.
 *
 * Bedragen als `number` in de DTO (serialisatie van Prisma `Decimal`); de
 * betaalrun-berekening (SEPA) gebeurt server-side op de DB-`Decimal`-waarden.
 */

import { z } from 'zod'
import { refineBetaaldOpNietToekomstig } from './inkoop'

// ─── Status (mirror Prisma enum InkoopBetaallijstStatus) ──────────────────────

export const INKOOP_BETAALLIJST_STATUS = ['CONCEPT', 'VERWERKT', 'GEANNULEERD'] as const
export type InkoopBetaallijstStatus = (typeof INKOOP_BETAALLIJST_STATUS)[number]

export const INKOOP_BETAALLIJST_STATUS_LABELS: Record<InkoopBetaallijstStatus, string> = {
  CONCEPT: 'Concept',
  VERWERKT: 'Verwerkt',
  GEANNULEERD: 'Geannuleerd',
}

export const INKOOP_BETAALLIJST_STATUS_BADGE_VARIANT: Record<
  InkoopBetaallijstStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  CONCEPT: 'info',
  VERWERKT: 'success',
  GEANNULEERD: 'secondary',
}

// ─── Filter (gedeeld met de inkoopfacturen-lijstfilters) ──────────────────────

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

/**
 * Het filter-blok dat een betaallijst kan voeden (spiegelt de lijstfilters).
 * Status is hier niet relevant: een betaallijst neemt altijd alleen de
 * te-betalen facturen (OPEN/GEBOEKT) mee, dat dwingt de server af.
 */
export const inkoopBetaallijstFilterSchema = z.object({
  leverancierId: z.string().min(1).optional(),
  entityId: z.string().min(1).optional(),
  factuurdatumVan: isoDate.optional(),
  factuurdatumTot: isoDate.optional(),
  vervaldatumVan: isoDate.optional(),
  vervaldatumTot: isoDate.optional(),
  search: z.string().trim().max(100).optional(),
})
export type InkoopBetaallijstFilter = z.infer<typeof inkoopBetaallijstFilterSchema>

/**
 * Create-payload: óf een handmatige selectie (`ids`), óf een `filter`. Minstens
 * één van beide is verplicht (anders zou een lege of alles-omvattende lijst
 * ontstaan). Gedeeld door de API (`.parse`) en de client-side bevestiging.
 */
export const createInkoopBetaallijstSchema = z
  .object({
    omschrijving: z.string().trim().max(200).optional(),
    ids: z.array(z.string().min(1)).optional(),
    filter: inkoopBetaallijstFilterSchema.optional(),
  })
  .refine((v) => (v.ids !== undefined && v.ids.length > 0) || v.filter !== undefined, {
    message: 'Selecteer facturen of geef een filter op om een betaallijst te maken.',
    path: ['ids'],
  })
export type CreateInkoopBetaallijstInput = z.infer<typeof createInkoopBetaallijstSchema>

export const markeerBetaallijstBetaaldSchema = z.object({
  toelichting: z.string().trim().max(2000).optional(),
  // FO-303-P3 — token uit de detail-preview; de server herberekent de SEPA + token
  // en weigert (409) als de betaalinhoud sinds de preview wijzigde (bv. een IBAN).
  previewToken: z.string().min(1).optional(),
  // batch-66 inkoop-afwijkingen (FO-490) — staat er op de lijst een factuur die
  // sinds het aanmaken een open blokkerende afwijking kreeg, dan beslist de
  // gebruiker expliciet: BETAALD (zij is via deze lijst toch betaald; de afwijking
  // blijft staan) of OPEN (niet betaald; de factuur blijft openstaan). Zonder
  // keuze weigert de server (409 AFWIJKING_OP_BETAALLIJST).
  afwijkingKeuze: z.enum(['BETAALD', 'OPEN']).optional(),
})
export type MarkeerBetaallijstBetaaldInput = z.infer<typeof markeerBetaallijstBetaaldSchema>

// FO-303-P3 — annuleren/afwijzen bij de bevestig-preview: verplichte reden (>= 5
// tekens, zelfde conventie als de verlof-afwijzing). De run vervalt (soft-delete).
export const annuleerBetaallijstSchema = z.object({
  reden: z.string().trim().min(5, 'Reden moet minimaal 5 tekens bevatten').max(1000),
  // batch-66 inkoop-afwijkingen (FO-490, herreview) — is er voor de lijst al een
  // betaalbestand (SEPA of Excel) gedownload, dan vervalt de lijst alleen na de
  // uitdrukkelijke bevestiging dat het bestand NIET bij de bank is aangeboden.
  // Anders staan alle facturen weer op te betalen en gaan ze een tweede keer mee.
  bevestigNietAangeboden: z.boolean().optional(),
})
export type AnnuleerBetaallijstInput = z.infer<typeof annuleerBetaallijstSchema>

/**
 * Bulk "markeer betaald" rechtstreeks vanuit de lijst-selectie. FO-081: één
 * betaaldatum voor de hele selectie, verplicht en niet-toekomstig (Europe/Amsterdam).
 */
export const inkoopFacturenBetaaldBulkSchema = z
  .object({
    ids: z.array(z.string().min(1)).min(1).max(500),
    betaaldOp: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    toelichting: z.string().trim().max(2000).optional(),
  })
  .superRefine((d, ctx) => refineBetaaldOpNietToekomstig(d.betaaldOp, ctx))
export type InkoopFacturenBetaaldBulkInput = z.infer<typeof inkoopFacturenBetaaldBulkSchema>

// ─── DTO's ────────────────────────────────────────────────────────────────────

export interface InkoopBetaallijstRegelDTO {
  id: string
  factuurId: string
  factuurNr: string
  leverancierNaam: string
  leverancierIban: string | null
  bvNaam: string | null
  referentie: string | null
  bedrag: number
  btwBedrag: number
  totaal: number
  factuurdatum: string
  vervaldatum: string | null
  /** Negatief totaal = creditnota (wordt verrekend, niet los betaald). */
  isCreditnota: boolean
}

export interface InkoopBetaallijstListRowDTO {
  id: string
  nummer: string
  status: InkoopBetaallijstStatus
  omschrijving: string | null
  /** Netto totaal (incl. eventuele negatieve creditsaldi). */
  totaal: number
  aantalFacturen: number
  aangemaaktDoorNaam: string | null
  excelGeexporteerdOp: string | null
  sepaGeexporteerdOp: string | null
  betaaldGemarkeerdOp: string | null
  createdAt: string
}

/** Reden waarom een (genette) crediteur niet in het SEPA-bestand komt. */
export const INKOOP_BETAALLIJST_SEPA_SKIP_REASON = [
  'GEEN_BV',
  'GEEN_DEBITEUR_IBAN',
  'ONGELDIGE_DEBITEUR_IBAN',
  'GEEN_CREDITEUR_IBAN',
  'ONGELDIGE_CREDITEUR_IBAN',
  'CREDITSALDO',
] as const
export type InkoopBetaallijstSepaSkipReason =
  (typeof INKOOP_BETAALLIJST_SEPA_SKIP_REASON)[number]

export const INKOOP_BETAALLIJST_SEPA_SKIP_LABELS: Record<
  InkoopBetaallijstSepaSkipReason,
  string
> = {
  GEEN_BV: 'Geen BV gekoppeld',
  GEEN_DEBITEUR_IBAN: 'BV heeft geen IBAN',
  ONGELDIGE_DEBITEUR_IBAN: 'BV-IBAN ongeldig',
  GEEN_CREDITEUR_IBAN: 'Leverancier heeft geen IBAN',
  ONGELDIGE_CREDITEUR_IBAN: 'Leverancier-IBAN ongeldig',
  CREDITSALDO: 'Creditsaldo (verrekend, niet betaald)',
}

export interface InkoopBetaallijstSepaOvergeslagenDTO {
  creditorName: string
  factuurNrs: string[]
  reason: InkoopBetaallijstSepaSkipReason
  nettoBedrag: number
}

/** Voorbeeld van wat het SEPA-bestand zal bevatten (server-side berekend). */
export interface InkoopBetaallijstSepaPreviewDTO {
  aantalTransacties: number
  totaalBetaalbaar: number
  overgeslagen: InkoopBetaallijstSepaOvergeslagenDTO[]
}

export interface InkoopBetaallijstDetailDTO extends InkoopBetaallijstListRowDTO {
  regels: InkoopBetaallijstRegelDTO[]
  sepaPreview: InkoopBetaallijstSepaPreviewDTO
  /**
   * FO-303-P3 — hash over de actuele SEPA-betaalinhoud. Gaat mee bij "markeer
   * betaald"; de server herberekent 'm en weigert (409) als de betaling sinds de
   * preview wijzigde (bv. een gecorrigeerde leverancier-IBAN).
   */
  previewToken: string
  /**
   * batch-66 inkoop-afwijkingen (FO-490) — facturen op deze (concept-)lijst die
   * NU niet (meer) voluit betaald mogen worden. Zolang dit niet leeg is, maakt de
   * server geen SEPA-bestand aan.
   *
   *  - `afgehandeld: false` = een open blokkerende afwijking of een deelbetaling:
   *    bij "markeer als betaald" beslist de gebruiker of zij via de lijst toch is
   *    betaald;
   *  - `afgehandeld: true` = sinds het maken van de lijst los betaald, geannuleerd
   *    of verwijderd: "markeer als betaald" slaat haar zelf over.
   */
  metAfwijking: { factuurNr: string; reden: string; afgehandeld: boolean }[]
}

export interface InkoopBetaallijstListMeta {
  total: number
  page: number
  pageSize: number
}
